import type { MouseEvent, MutableRefObject } from 'react';
import { useRef } from 'react';

const OFFSET = 5;
const POW_2 = 2;

interface HookResult<T> {
    isDraggedRef: MutableRefObject<boolean>;
    onMouseDown: (event: MouseEvent<T>) => void;
    onMouseUp: (event: MouseEvent<T>) => void;
}

/**
 * A custom React hook that detects whether an element has been dragged.
 *
 * This hook tracks mouse down and up events to determine if an element
 * has been dragged. It considers a drag to have occurred if the mouse
 * has moved more than 5 pixels between mousedown and mouseup events.
 *
 * @template T - The type of the element being tracked (e.g., HTMLAnchorElement)
 * @returns {HookResult<T>} An object containing:
 *   - isDraggedRef: A mutable ref object with a boolean value indicating if a drag occurred
 *   - onMouseDown: A function to be used as the onMouseDown event handler
 *   - onMouseUp: A function to be used as the onMouseUp event handler
 *
 * @example
 * // Real-world usage in a DragGuardLink component
 * export const DragGuardLink: WrapperFC<LinkProps> = (passThroughProps) => {
 *   const { isDraggedRef, onMouseDown, onMouseUp } = useIsDragged<HTMLAnchorElement>();
 *
 *   const handleClickCapture = useCallback(
 *     (event: MouseEvent<HTMLAnchorElement>) => {
 *       if (isDraggedRef.current) {
 *         event.preventDefault();
 *         event.stopPropagation();
 *       }
 *     },
 *     [isDraggedRef],
 *   );
 *
 *   return (
 *     <Link
 *       onMouseDownCapture={onMouseDown}
 *       onMouseUpCapture={onMouseUp}
 *       onClickCapture={handleClickCapture}
 *       {...passThroughProps}
 *     />
 *   );
 * };
 *
 */
export function useIsDragged<T>(): HookResult<T> {
    const isDragged = useRef(false);
    const mouseDownPosition = useRef({
        x: 0,
        y: 0,
    });

    const onMouseDown = (event: MouseEvent<T>): void => {
        isDragged.current = false;

        mouseDownPosition.current = {
            x: event.clientX,
            y: event.clientY,
        };
    };

    const onMouseUp = (event: MouseEvent<T>): void => {
        const distance = Math.sqrt(
            (mouseDownPosition.current.y - event.clientY) ** POW_2 +
                (mouseDownPosition.current.x - event.clientX) ** POW_2,
        );

        isDragged.current = distance > OFFSET;
    };

    return { isDraggedRef: isDragged, onMouseDown, onMouseUp };
}
